/**
 * Web Speech API（ブラウザ内蔵の音声認識）による音声入力フック。
 *
 * Chrome / Edge / Safari が対応し、Firefox は未対応。未対応環境では
 * `isSupported` が false になるので、呼び出し側はマイクボタン自体を
 * 出さない運用にする。
 *
 * 認識結果は `onResult(finalChunk, interim)` で通知する:
 *  - `finalChunk` … 今回のイベントで新たに確定した分（差分。空文字もあり得る）
 *  - `interim`    … まだ確定していない認識中のテキスト（次のイベントで置き換わる）
 *
 * 累積ではなく差分を渡すのは、呼び出し側が「確定分は本文へ確定挿入し、未確定分は
 * 毎回描き替える」という扱いを素直に書けるようにするため。
 */

import { useCallback, useEffect, useRef, useState } from "react";

// lib.dom.d.ts には SpeechRecognition 本体とイベントの型が無いため（結果側の
// SpeechRecognitionResultList 等はある）、必要な分だけここで補う。
interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const getSpeechRecognitionConstructor =
  (): SpeechRecognitionConstructor | null => {
    if (typeof window === "undefined") return null;
    const candidate = window as unknown as {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    return (
      candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null
    );
  };

/** 呼び出し側が文言を出し分けるための、正規化したエラー種別。 */
export type SpeechRecognitionErrorReason =
  | "not-allowed"
  | "no-speech"
  | "audio-capture"
  | "network"
  | "unknown";

const ERROR_REASONS: Record<string, SpeechRecognitionErrorReason> = {
  "not-allowed": "not-allowed",
  "service-not-allowed": "not-allowed",
  "no-speech": "no-speech",
  "audio-capture": "audio-capture",
  network: "network",
};

export interface UseSpeechRecognitionOptions {
  /** 認識言語（既定: ja-JP） */
  lang?: string;
  /** 認識結果の通知。finalChunk は新たに確定した差分、interim は認識中のテキスト。 */
  onResult: (finalChunk: string, interim: string) => void;
  /** マイク拒否・通信エラー等。表示文言は呼び出し側が決める。 */
  onError?: (reason: SpeechRecognitionErrorReason) => void;
}

export interface UseSpeechRecognitionResult {
  /** この環境で音声認識が使えるか */
  isSupported: boolean;
  /** 認識セッション中か */
  isListening: boolean;
  start: () => void;
  stop: () => void;
}

export const useSpeechRecognition = ({
  lang = "ja-JP",
  onResult,
  onError,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionResult => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  // コールバックは毎レンダーで作り替えられるため、認識インスタンスからは
  // ref 経由で常に最新を呼ぶ（イベントハンドラを貼り替えずに済む）。
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const isSupported = getSpeechRecognitionConstructor() !== null;

  const stop = useCallback(() => {
    // stop() は「確定結果を1回返してから終了」なので abort() は使わない
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (recognitionRef.current) return;
    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) return;

    const recognition = new Recognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let finalChunk = "";
      let interim = "";
      // resultIndex 以降が今回のイベントで更新された分
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) finalChunk += result[0].transcript;
        else interim += result[0].transcript;
      }
      onResultRef.current(finalChunk, interim);
    };

    recognition.onerror = (event) => {
      // 無音タイムアウトは continuous 指定でも起き得る。呼び出し側が
      // 「エラー扱いしない」判断をできるよう種別だけ渡す。
      onErrorRef.current?.(ERROR_REASONS[event.error] ?? "unknown");
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
  }, [lang]);

  // アンマウント時は結果を捨てて即終了させる
  useEffect(
    () => () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      recognitionRef.current = null;
    },
    [],
  );

  return { isSupported, isListening, start, stop };
};

export default useSpeechRecognition;
